import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  CirclesGameSession,
  type CirclesSessionStatus,
} from '../../game/circles/circlesSession';
import { getBuiltinCirclesMapBySlug } from '@shared/circlesBuiltinMaps';
import { stripVariantSuffix } from '@shared/chart';
import { saveModePref } from '../../game/modePref';
import { modePath } from '../../game/modeRouting';
import { markCirclesSliderTutorialSeen } from '../../game/tutorialFlag';
import { buildSliderTutorialCirclesChart } from '../../game/circles/tutorialCirclesChart';
import '../play.css';
import './circlesTutorial.css';

const TUTORIAL_CHART = buildSliderTutorialCirclesChart();

/**
 * Slider onboarding for Circles mode. The play page diverts here the
 * first time a player is about to enter a tier that contains sliders
 * (hard / extreme / insane); the slider-free `normal` tier never lands
 * here. The `?next=<slug>` param carries the chart the player was
 * heading for so we can drop them straight into it once the lesson is
 * done or skipped.
 *
 * Mirrors {@link import('./CirclesTutorialPage').CirclesTutorialPage} —
 * the only differences are the slider-focused chart + copy and the
 * "continue to the song you picked" exit instead of "browse tracks".
 */
export function CirclesSliderTutorialPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextSlug = searchParams.get('next');
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const sessionRef = useRef<CirclesGameSession | null>(null);
  const [needsTap, setNeedsTap] = useState(true);
  const [status, setStatus] = useState<CirclesSessionStatus>('idle');
  const isTouch = useMemo(
    () => typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches,
    [],
  );

  // Title of the track they were heading for, for the "continue" CTA.
  const nextTitle = useMemo(() => {
    if (!nextSlug) {
      return null;
    }
    const builtin = getBuiltinCirclesMapBySlug(nextSlug);
    return builtin ? stripVariantSuffix(builtin.chart) : null;
  }, [nextSlug]);

  useEffect(() => {
    saveModePref('circles');
  }, []);

  useEffect(() => {
    return () => {
      if (sessionRef.current) {
        void sessionRef.current.destroy();
        sessionRef.current = null;
      }
    };
  }, []);

  // Single exit path to the song the player picked (or home when they
  // arrived without one). Always marks the slider tutorial seen first so
  // the play-page gate doesn't bounce them straight back here — that
  // would otherwise be an infinite loop on a failed attempt.
  const proceed = useCallback(() => {
    markCirclesSliderTutorialSeen();
    if (nextSlug) {
      navigate(modePath('circles', `play/${nextSlug}`), { replace: true });
    } else {
      navigate(modePath('circles'));
    }
  }, [navigate, nextSlug]);

  const startTutorial = useCallback(async () => {
    if (!canvasRef.current) {
      return;
    }
    setNeedsTap(false);
    if (sessionRef.current) {
      await sessionRef.current.destroy();
      sessionRef.current = null;
    }
    const session = new CirclesGameSession({
      chart: TUTORIAL_CHART,
      canvas: canvasRef.current,
      audioOffsetMs: 0,
      onStatusChange: (s) => setStatus(s),
      onComplete: (outcome) => {
        // Clearing the lesson marks it seen immediately so a later
        // browser-back or direct nav doesn't re-gate. A failed attempt
        // stays unmarked here; the player can replay, and only the
        // explicit "continue" button (via proceed) marks it.
        if (outcome.completed) {
          markCirclesSliderTutorialSeen();
        }
      },
    });
    sessionRef.current = session;
    try {
      await session.start();
    } catch (err) {
      console.error('Failed to start Circles slider tutorial:', err);
      setNeedsTap(true);
    }
  }, []);

  useEffect(() => {
    if (!needsTap) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === ' ' || e.code === 'Space' || e.key === 'Enter') {
        e.preventDefault();
        void startTutorial();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [needsTap, startTutorial]);

  return (
    <div className="play-page tutorial-page" data-testid="circles-slider-tutorial-page">
      <div className="play-canvas-wrap">
        <canvas
          ref={canvasRef}
          className="play-canvas"
          data-testid="circles-slider-tutorial-canvas"
        />

        {needsTap && (
          <div className="play-overlay" data-testid="circles-slider-tutorial-start-overlay">
            <div className="play-overlay-card">
              <h2>Sliders ahead</h2>
              <p>
                This difficulty adds <strong>sliders</strong>. {isTouch ? 'Tap' : 'Click'} the
                slider head, then hold and drag so the cursor follows the ball along the
                path.
              </p>
              <ul className="circles-tutorial-rules">
                <li>
                  <strong>Head:</strong> {isTouch ? 'tap' : 'click'} it on the beat, just
                  like a circle.
                </li>
                <li>
                  <strong>Body:</strong> keep holding and track the ball to the tail to
                  bank bonus points.
                </li>
                <li>
                  <strong>Reverse arrow:</strong> when the ball reaches the end, follow it
                  back the other way.
                </li>
              </ul>
              <p className="muted faint">
                Quick lesson on a slow track &middot; headphones recommended.
              </p>
              <div className="play-overlay-actions">
                <button
                  type="button"
                  className="btn btn-primary btn-large"
                  onClick={() => void startTutorial()}
                  data-testid="circles-slider-tutorial-start"
                >
                  Start slider lesson
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={proceed}
                  data-testid="circles-slider-tutorial-skip"
                >
                  {nextTitle ? `Skip — play ${nextTitle}` : 'Skip — take me to the song'}
                </button>
              </div>
            </div>
          </div>
        )}

        {(status === 'completed' || status === 'failed') && (
          <div className="play-overlay" data-testid="circles-slider-tutorial-complete-overlay">
            <div className="play-overlay-card">
              <h2>{status === 'completed' ? 'Nicely traced' : 'Give it another go?'}</h2>
              <p>
                {status === 'completed'
                  ? 'That’s all there is to sliders — click the head and follow the ball.'
                  : 'No pressure — you can replay the lesson or jump straight into the song.'}
              </p>
              <div className="play-overlay-actions">
                <button
                  type="button"
                  className="btn btn-primary btn-large"
                  onClick={proceed}
                  data-testid="circles-slider-tutorial-continue"
                >
                  {nextTitle ? `Play ${nextTitle}` : 'Continue'}
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => {
                    setNeedsTap(true);
                    setStatus('idle');
                  }}
                >
                  Replay lesson
                </button>
                <Link to={modePath('circles')} className="btn btn-ghost">
                  Back to Circles
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
